import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FullScreenService {

  currentFullScreenStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private fullScreenListener: () => void;

  constructor() {
    // Listener to update the fullscreen status
    this.fullScreenListener = () => {
      this.currentFullScreenStatus.next(!!document.fullscreenElement);
      document.removeEventListener("fullscreenchange", this.fullScreenListener);
    };
    document.addEventListener("fullscreenchange", this.fullScreenListener);
  }

  // Returns an observable for the current fullscreen status
  fullScreenStatus(): Observable<boolean> {
    return this.currentFullScreenStatus.asObservable();
  }

  // Toggles fullscreen mode on or off
  toggleFullScreen() {
    if (!this.currentFullScreenStatus.getValue()) {
      // If not in fullscreen, attempt to enter fullscreen mode
      this.enterFullScreen();
    } else {
      this.exitFullscreen();
    }
  }

  /**
   * Attempts to enter fullscreen mode.
   * Returns a Promise that resolves to true if entering fullscreen succeeded,
   * or false if it failed (likely because a user gesture is required).
   */
  enterFullScreen(): Promise<boolean> {
    const elem = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void>;
      msRequestFullscreen?: () => Promise<void>;
    };

    if (elem.requestFullscreen) {
      return elem.requestFullscreen().then(() => {
        this.currentFullScreenStatus.next(true);
        return true;
      }).catch((error) => {
        // console.error("Error trying to enter fullscreen mode:", error);
        return false;
      });
    } else if (elem.webkitRequestFullscreen) {
      return elem.webkitRequestFullscreen().then(() => {
        this.currentFullScreenStatus.next(true);
        return true;
      }).catch((error) => {
        // console.error("Error trying to enter fullscreen mode on Safari:", error);
        return false;
      });
    } else if (elem.msRequestFullscreen) {
      return elem.msRequestFullscreen().then(() => {
        this.currentFullScreenStatus.next(true);
        return true;
      }).catch((error) => {
        // console.error("Error trying to enter fullscreen mode on IE:", error);
        return false;
      });
    }

    // If no fullscreen method is available, resolve as false.
    return Promise.resolve(false);
  }

  /**
   * Exits fullscreen mode.
   */
  exitFullscreen() {
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void>;
      msExitFullscreen?: () => Promise<void>;
    };
    if (doc.exitFullscreen) {
      doc.exitFullscreen().then(() => {
        this.currentFullScreenStatus.next(false);
      }).catch((error) => {
        // console.error("Error trying to exit fullscreen mode:", error);
      });
    } else if (doc.webkitExitFullscreen) {
      doc.webkitExitFullscreen().then(() => {
        this.currentFullScreenStatus.next(false);
      }).catch((error) => {
        // console.error("Error trying to exit fullscreen mode on Safari:", error);
      });
    } else if (doc.msExitFullscreen) {
      doc.msExitFullscreen().then(() => {
        this.currentFullScreenStatus.next(false);
      }).catch((error) => {
        // console.error("Error trying to exit fullscreen mode on IE:", error);
      });
    }
  }
}
